import { applyPatches, enablePatches, type Patch, produceWithPatches } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { PersistedItem } from '../types/DataModel';

interface UseUndoRedoReturn {
    undo: (e?: MouseEvent) => void;
    redo: (e?: MouseEvent) => void;
    canUndo: boolean;
    canRedo: boolean;
}

// Coalesce rapid edits (e.g. typing) into a single undo step.
const SNAPSHOT_DEBOUNCE_MS = 400;
// Bound history.
const MAX_STACK_SIZE = 100;

interface HistoryEntry {
    redo: Patch[];
    undo: Patch[];
}

enablePatches();

// RHF's `getValues()` returns a SHALLOW merge of defaults and current values
// (`{...b, ...x}`); nested objects share references with its internal
// `_formValues`, which RHF mutates in place on every `setValue`. We must take
// a deep clone before stashing the value as our diff baseline, otherwise the
// next setValue would mutate the snapshot and the diff would see prev===next.
const cloneSnapshot = <T,>(value: T): T => {
    if (value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(cloneSnapshot) as unknown as T;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
        out[key] = cloneSnapshot((value as Record<string, unknown>)[key]);
    }
    return out as T;
};

// Mutate `draft` so that it deep-equals `next`, walking only into subtrees that
// actually differ. Doing it this way lets Immer record minimal patches (one
// per primitive that changed) instead of a single top-level "replace" that
// would store the whole document.
const reconcile = (draft: unknown, next: unknown): void => {
    if (draft === next) return;

    if (
        typeof draft !== 'object' || draft === null ||
        typeof next !== 'object' || next === null ||
        Array.isArray(draft) !== Array.isArray(next)
    ) {
        // Caller must replace at the parent level; leaves are handled there.
        return;
    }

    if (Array.isArray(draft) && Array.isArray(next)) {
        const draftArr = draft as unknown[];
        const nextArr = next as unknown[];
        if (draftArr.length !== nextArr.length) {
            draftArr.length = nextArr.length;
        }
        for (let i = 0; i < nextArr.length; i++) {
            const dv = draftArr[i];
            const nv = nextArr[i];
            if (dv === nv) continue;
            if (
                typeof dv === 'object' && dv !== null &&
                typeof nv === 'object' && nv !== null &&
                Array.isArray(dv) === Array.isArray(nv)
            ) {
                reconcile(dv, nv);
            } else {
                draftArr[i] = nv;
            }
        }
        return;
    }

    const draftObj = draft as Record<string, unknown>;
    const nextObj = next as Record<string, unknown>;

    for (const key of Object.keys(nextObj)) {
        const dv = draftObj[key];
        const nv = nextObj[key];
        if (dv === nv) continue;
        if (
            typeof dv === 'object' && dv !== null &&
            typeof nv === 'object' && nv !== null &&
            Array.isArray(dv) === Array.isArray(nv)
        ) {
            reconcile(dv, nv);
        } else {
            draftObj[key] = nv;
        }
    }
    for (const key of Object.keys(draftObj)) {
        if (!Object.prototype.hasOwnProperty.call(nextObj, key)) {
            delete draftObj[key];
        }
    }
};

const diff = (prev: PersistedItem, next: PersistedItem): HistoryEntry | null => {
    const [, redoPatches, undoPatches] = produceWithPatches(prev, draft => {
        reconcile(draft, next);
    });
    if (redoPatches.length === 0) return null;
    return { redo: redoPatches, undo: undoPatches };
};

export const useUndoRedo = (): UseUndoRedoReturn => {
    const formContext = useFormContext();
    // RHF's `useForm` returns a fresh object every render; capturing the
    // methods directly in deps would invalidate effects (and cancel the
    // pending debounce timer) on every unrelated re-render of the form
    // provider. Stash them in a ref so callbacks always reach the latest
    // instance without triggering effect re-runs.
    const formRef = useRef(formContext);
    formRef.current = formContext;

    // Stacks live in refs - mutating them must NOT cause a re-render of every
    // component that uses this hook on every keystroke.
    const undoStackRef = useRef<HistoryEntry[]>([]);
    const redoStackRef = useRef<HistoryEntry[]>([]);
    const baselineRef = useRef<PersistedItem | null>(null);
    // Boolean (not counter) because RHF emits an unbounded number of watch
    // events while a single `reset` is in flight; we want to swallow ALL of
    // them for the duration of our own restore call.
    const inSelfResetRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const syncAvailability = useCallback(() => {
        const nextCanUndo = undoStackRef.current.length > 0;
        const nextCanRedo = redoStackRef.current.length > 0;
        setCanUndo(prev => (prev === nextCanUndo ? prev : nextCanUndo));
        setCanRedo(prev => (prev === nextCanRedo ? prev : nextCanRedo));
    }, []);

    const clearStacks = useCallback(() => {
        if (undoStackRef.current.length > 0) undoStackRef.current = [];
        if (redoStackRef.current.length > 0) redoStackRef.current = [];
        syncAvailability();
    }, [syncAvailability]);

    // Flush any pending debounced diff into the undo stack synchronously.
    // Called both by the debounce timer and by undo/redo before they read the
    // stacks - otherwise an undo issued mid-typing-burst would compute against
    // a stale baseline and silently drop the in-flight change.
    const commitPending = useCallback(() => {
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const previous = baselineRef.current;
        const current = cloneSnapshot(formRef.current.getValues() as PersistedItem);

        if (previous === null) {
            baselineRef.current = current;
            return;
        }

        const entry = diff(previous, current);
        baselineRef.current = current;
        if (entry === null) return;

        const stack = undoStackRef.current;
        stack.push(entry);
        if (stack.length > MAX_STACK_SIZE) {
            stack.splice(0, stack.length - MAX_STACK_SIZE);
        }
        if (redoStackRef.current.length > 0) redoStackRef.current = [];
    }, []);

    useEffect(() => {
        baselineRef.current = cloneSnapshot(formRef.current.getValues() as PersistedItem);

        const subscription = formRef.current.watch((_values, info) => {
            // Our own undo/redo `restore` triggers `reset`, which can fire
            // multiple watch events synchronously inside one call. Swallow
            // the entire reset window without recording.
            if (inSelfResetRef.current) {
                baselineRef.current = cloneSnapshot(formRef.current.getValues() as PersistedItem);
                return;
            }

            // A programmatic reset (e.g. loading a different item via the
            // navigation dropdown) emits with no field name. Treat it as a
            // fresh baseline and drop history that belongs to the old item.
            if (info.name === undefined) {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                    debounceTimerRef.current = null;
                }
                baselineRef.current = cloneSnapshot(formRef.current.getValues() as PersistedItem);
                clearStacks();
                return;
            }

            // RHF only sets `info.type` ('change') for Controller/register onChange events;
            // programmatic setValue has no type and must not record an undo entry (LAX-9905).
            if (info.type === undefined) {
                if (debounceTimerRef.current === null) {
                    baselineRef.current = cloneSnapshot(formRef.current.getValues() as PersistedItem);
                }
                return;
            }

            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                commitPending();
                syncAvailability();
            }, SNAPSHOT_DEBOUNCE_MS);
        });

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            subscription.unsubscribe();
        };
        // Mount-only on purpose: form methods are accessed via formRef so the
        // subscription is stable for the hook's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const restore = useCallback((next: PersistedItem) => {
        inSelfResetRef.current = true;
        try {
            formRef.current.reset(next, {
                keepDirty: true,
                keepTouched: false,
                keepValues: false,
            });
        } finally {
            inSelfResetRef.current = false;
        }
        baselineRef.current = cloneSnapshot(next);
    }, []);

    const undo = useCallback((e?: MouseEvent) => {
        e?.preventDefault?.();
        commitPending();
        const entry = undoStackRef.current.pop();
        if (entry === undefined) {
            syncAvailability();
            return;
        }

        const current = formRef.current.getValues() as PersistedItem;
        const next = applyPatches(current, entry.undo);
        redoStackRef.current.push(entry);
        restore(next);
        syncAvailability();
    }, [commitPending, restore, syncAvailability]);

    const redo = useCallback((e?: MouseEvent) => {
        e?.preventDefault?.();
        commitPending();
        const entry = redoStackRef.current.pop();
        if (entry === undefined) {
            syncAvailability();
            return;
        }

        const current = formRef.current.getValues() as PersistedItem;
        const next = applyPatches(current, entry.redo);
        undoStackRef.current.push(entry);
        restore(next);
        syncAvailability();
    }, [commitPending, restore, syncAvailability]);

    return { undo, redo, canUndo, canRedo };
};
